/**
 * Evaluate a parsed condition (ast.ts) the way the engine does — WITHOUT eval.
 *
 * The backend builds a JS script and runs it in Nashorn (`ExpressionEngine.evaluate`); the
 * live frontend uses `new Function`. Neither may appear here (TODO P2-1): once trees are
 * admin-writable data, evaluating a stored string would run a database row in the BD's
 * browser. The tree has a closed set of node kinds, so walking it is enough.
 *
 * Semantics are JavaScript's, on purpose, because that is what Nashorn gives the stored
 * rules — including the parts that make the live defects behave as they do:
 *   - an unanswered question is `undefined`; arithmetic on it is NaN and every comparison
 *     with NaN is false
 *   - `a || b` returns the first TRUTHY operand, so `a1 || b1 + a2 || b2` is a truthiness
 *     chain, and `0 || x` skips a real zero
 *   - `+` concatenates when either side is a string
 *   - `.includes` on a missing answer throws (TypeError) — at submit the backend would fail
 *     the request, so it is reported as an error, not as false
 *   - only a result that is exactly `true` counts (`result instanceof Boolean && result`)
 *
 * The injected helpers mirror ExpressionEngine: `hasKey(k)` is an own property of the
 * context (answers AND already-granted program names, which QuestionnaireProcessor writes
 * back as `context.put(program, 1)`), `hasAnyMatch(ref, set)` is "the multi-select answer
 * shares any value with `set`", false when the answer is not an array.
 */
import { type Expr, parse, refsOf } from './ast';

/** The evaluation context, keyed like `metric`: qualifiedNodeId → answer, program → 1. */
export type Metric = Record<string, unknown>;

/* eslint-disable @typescript-eslint/no-explicit-any -- JS operator semantics are the point */
/**
 * An answer not given yet but still to come. Evaluation is THREE-valued: a read of such a
 * ref yields `PENDING`, which propagates through arithmetic and comparisons, while `&&` /
 * `||` let a definite operand decide wherever it sits — `L >= 5 || A === 'a'` with A = 'a'
 * is a hit even though L comes first, and `A === 'a' && L >= 5` with A = 'b' is a miss.
 * Only a result that is still `PENDING` at the top makes the rule pending.
 *
 * `pendingRefs` is module state rather than a parameter only because the evaluator is
 * synchronous and re-entrancy-free; it is set and cleared around each evaluation.
 */
/**
 * A pending value carries the refs it is waiting on, so the 等待 list names only the answers
 * that can still change the outcome: an operand decided by short-circuiting drops out,
 * together with whatever it had been waiting on.
 */
class Pending {
  constructor(readonly refs: ReadonlySet<string>) {}
}
const isPending = (v: unknown): v is Pending => v instanceof Pending;
/** Merge the pending operands among `vs` (the others are definite and add nothing). */
const pendingOf = (vs: unknown[]): Pending =>
  new Pending(new Set(vs.filter(isPending).flatMap((p) => [...p.refs])));
let pendingRefs: ReadonlySet<string> | null = null;

function readRef(m: Metric, ref: string): any {
  if (pendingRefs?.has(ref)) return new Pending(new Set([ref]));
  return Object.prototype.hasOwnProperty.call(m, ref) ? m[ref] : undefined;
}

/**
 * The keyword `true`. ast.ts stores it as the string literal `'true'` (its `lit` holds strings
 * and numbers only), so in a BOOLEAN position — the whole expression, or an operand of
 * `&&` / `||` / `!` — it must be read back as the boolean, or the `true` catch-all branch
 * the editor appends would never be taken. In a comparison it stays the string.
 */
const isTrueKeyword = (e: Expr) => e.k === 'lit' && e.v === 'true';
function asBool(e: Expr, m: Metric): any {
  return isTrueKeyword(e) ? true : value(e, m);
}

function value(e: Expr, m: Metric): any {
  switch (e.k) {
    case 'var':
      return readRef(m, e.ref);
    case 'lit':
      return e.v;
    case 'cmp': {
      const l = value(e.l, m);
      const r = value(e.r, m);
      if (isPending(l) || isPending(r)) return pendingOf([l, r]);
      switch (e.op) {
        case '===':
          return l === r;
        case '>=':
          return l >= r;
        case '>':
          return l > r;
        case '<=':
          return l <= r;
        case '<':
          return l < r;
      }
      return false;
    }
    case 'and': {
      // JS returns the first falsy operand, else the last. A definite falsy anywhere decides
      // it; otherwise any pending operand leaves it pending.
      let v: any = true;
      const seen: unknown[] = [];
      for (const x of e.xs) {
        v = asBool(x, m);
        if (isPending(v)) seen.push(v);
        else if (!v) return v;
      }
      return seen.length ? pendingOf(seen) : v;
    }
    case 'or':
    case 'coalesce': {
      // First truthy operand wins wherever it sits; otherwise pending if any operand was.
      let v: any = false;
      const seen: unknown[] = [];
      for (const x of e.xs) {
        v = asBool(x, m);
        if (isPending(v)) seen.push(v);
        // A coalesce returns the first truthy VALUE, so an earlier pending operand could
        // still be the one it returns; a logical or is decided by any truthy operand.
        else if (v) return seen.length && e.k === 'coalesce' ? pendingOf(seen) : v;
      }
      return seen.length ? pendingOf(seen) : v;
    }
    case 'not': {
      const v = asBool(e.x, m);
      return isPending(v) ? v : !v;
    }
    case 'add':
    case 'mul': {
      const xs = e.xs.map((x) => value(x, m));
      if (xs.some(isPending)) return pendingOf(xs);
      return e.k === 'add' ? xs.reduce((a, b) => a + b) : xs.reduce((a, b) => a * b);
    }
    case 'has': {
      const x = value(e.x, m);
      if (isPending(x)) return x;
      if (x === undefined || x === null || typeof x.includes !== 'function') {
        throw new TypeError(`对未作答的值调用 .includes('${e.v}')`);
      }
      return x.includes(e.v);
    }
    case 'hasKey':
      return Object.prototype.hasOwnProperty.call(m, e.program);
    case 'anyMatch': {
      const v = readRef(m, e.ref);
      if (isPending(v)) return v;
      return Array.isArray(v) && v.some((x) => e.vs.includes(x));
    }
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export type EvalResult =
  | { status: 'hit' }
  | { status: 'miss' }
  /** Reads a question that has not been answered yet but still can be. */
  | { status: 'pending'; waitingFor: string[] }
  | { status: 'error'; message: string };

export interface EvalOptions {
  /**
   * Is this unanswered question still going to be asked? Its refs make the rule PENDING
   * rather than evaluated. A question on a branch the walk did not take is not — it stays
   * `undefined`, exactly as Nashorn sees it at submit.
   */
  stillAskable?: (qualifiedId: string) => boolean;
  isAnswered?: (qualifiedId: string) => boolean;
}

/** Parse + evaluate one stored expression. A parse failure is an error result. */
export function evaluateExpression(expression: string, metric: Metric, opts: EvalOptions = {}): EvalResult {
  let tree: Expr;
  try {
    tree = parse(expression.trim());
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : String(err) };
  }
  const answered = opts.isAnswered ?? ((q: string) => metric[q] !== undefined);
  const waiting = refsOf(tree).filter((q) => !answered(q) && (opts.stillAskable?.(q) ?? false));
  pendingRefs = waiting.length ? new Set(waiting) : null;
  try {
    const v = asBool(tree, metric);
    // Pending only when the outcome really depends on an answer still to come.
    if (isPending(v)) return { status: 'pending', waitingFor: waiting.filter((q) => v.refs.has(q)) };
    return v === true ? { status: 'hit' } : { status: 'miss' };
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : String(err) };
  } finally {
    pendingRefs = null;
  }
}
